"use client";

import type { FC, KeyboardEvent, ReactNode } from "react";
import { useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import * as RadixPopover from "@radix-ui/react-popover";
import { Checkbox } from "@/components/checkbox";
import { Tag } from "@/components/tag";
import { cn } from "@/lib/cn";
import { FieldLabel, FieldMessage, POPOVER_PANEL_WIDTH, type FieldState } from "./field-chrome";

type MultiSelectOption = {
  value: string;
  label: string;
};

type MultiSelectProps = {
  /** Classes for the trigger — the bordered field itself. */
  className?: string;
  /**
   * Classes for the label + field + message stack. A row of fields whose labels differ in height
   * passes `grid-rows-subgrid` here so every label and every field line up with its neighbours.
   */
  containerClassName?: string;
  label?: ReactNode;
  message?: ReactNode;
  state?: FieldState;
  required?: boolean;
  placeholder?: string;
  options: MultiSelectOption[];
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (values: string[]) => void;
  maxItems?: number;
  /**
   * How many chips the closed field draws before the rest collapse into a `+n` count. The
   * directory's filters are the reason it exists: a visitor may tick a dozen specialties, and a
   * field that grows a row per tick pushes the listing off the screen. `0` disables the cap.
   */
  maxVisibleTags?: number;
};

const DEFAULT_MAX_VISIBLE_TAGS = 4;

export const MultiSelect: FC<MultiSelectProps> = ({
  className,
  containerClassName,
  label,
  message,
  state = "default",
  required,
  placeholder = "Please input",
  options,
  value,
  defaultValue = [],
  onValueChange,
  maxItems,
  maxVisibleTags = DEFAULT_MAX_VISIBLE_TAGS,
}) => {
  const [open, setOpen] = useState(false);
  const [internal, setInternal] = useState<string[]>(defaultValue);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const selected = value ?? internal;
  const atCap = maxItems !== undefined && selected.length >= maxItems;

  const selectedOptions = options.filter((option) => selected.includes(option.value));
  const visible = maxVisibleTags > 0 ? selectedOptions.slice(0, maxVisibleTags) : selectedOptions;
  const hidden = selectedOptions.length - visible.length;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) setHighlightedIndex(0);
  };

  const toggle = (optionValue: string) => {
    const isSelected = selected.includes(optionValue);
    if (!isSelected && atCap) return;
    const next = isSelected ? selected.filter((v) => v !== optionValue) : [...selected, optionValue];
    setInternal(next);
    onValueChange?.(next);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((index) => Math.min(index + 1, options.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((index) => Math.max(index - 1, 0));
        break;
      case "Enter":
      case " ": {
        const option = options[highlightedIndex];
        if (option) {
          event.preventDefault();
          toggle(option.value);
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className={cn("flex w-full flex-col gap-8", containerClassName)}>
      <FieldLabel state={state} required={required}>
        {label}
      </FieldLabel>
      <RadixPopover.Root open={open} onOpenChange={handleOpenChange}>
        {/* Figma 931:54123. `min-h-62` keeps an empty field the same height as a plain select
            beside it; the chips live inside that padding rather than adding to it. */}
        <RadixPopover.Trigger
          className={cn(
            "flex min-h-62 w-full cursor-pointer items-center justify-between gap-8 rounded-medium border bg-neutral-white p-16 text-left outline-none focus:border-brand-blue",
            state === "error" ? "border-brand-red" : "border-neutral-mid-grey",
            className,
          )}
        >
          {selected.length === 0 ? (
            <span className="text-16 text-neutral-black/35">{placeholder}</span>
          ) : (
            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-8">
              {visible.map((option) => (
                <Tag
                  key={option.value}
                  size="s"
                  className="max-w-full"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggle(option.value);
                  }}
                >
                  <span className="truncate">{option.label}</span>
                </Tag>
              ))}
              {hidden > 0 && (
                <Tag size="s" iconRight={false} className="text-neutral-black/70">
                  +{hidden}
                </Tag>
              )}
            </span>
          )}
          <ChevronDownIcon className="size-24 shrink-0 text-neutral-black" />
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content
            align="start"
            side="bottom"
            sideOffset={8}
            collisionPadding={16}
            onKeyDown={handleKeyDown}
            /* `pointer-events-auto` is load-bearing inside a modal dialog: Radix's Dialog puts
               `pointer-events: none` on the body while it is open, and this content is portaled to
               the body — without it, every option in a dropdown opened from the filters sheet
               ignores clicks. */
            className={cn(
              "pointer-events-auto z-50 rounded-small border border-neutral-light-grey bg-neutral-white p-8 shadow-lg data-[state=closed]:animate-[dropdown-out_150ms_ease-in] data-[state=open]:animate-[dropdown-in_150ms_ease-out]",
              POPOVER_PANEL_WIDTH,
            )}
          >
            <div className="flex max-h-280 flex-col gap-4 overflow-y-auto">
              {options.map((option, index) => {
                const isSelected = selected.includes(option.value);
                const disabled = !isSelected && atCap;

                return (
                  <label
                    key={option.value}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={cn(
                      "flex items-center gap-8 rounded-small px-8 py-8 transition-colors duration-100",
                      disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
                      index === highlightedIndex && "bg-tone-aqua-tint",
                    )}
                  >
                    <Checkbox size="m" checked={isSelected} disabled={disabled} onCheckedChange={() => toggle(option.value)} />
                    <span className="text-16 text-neutral-black">{option.label}</span>
                  </label>
                );
              })}
            </div>
            {maxItems !== undefined && (
              <p className="px-8 pt-8 text-14 text-neutral-grey">
                {selected.length}/{maxItems} selected
              </p>
            )}
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
      <FieldMessage state={state}>{message}</FieldMessage>
    </div>
  );
};
